import type {
	UploadDiscoveryState,
	UploadFileStatus,
} from '../../../context/progressContext'
import type { UploadStatus } from './types'

export type UploadCompletionState = 'success' | 'warning' | 'failed' | null

export type FolderUploadOutcome = {
	status: UploadStatus
	isWarning: boolean
	label: string
}

type UploadStatusCounts = Record<UploadStatus, number>

export type UploadProgressSummary = {
	fileItems: UploadFileStatus[]
	counts: UploadStatusCounts
	overallProgress: number
	hasActiveUploads: boolean
	isUploadFinished: boolean
	completionState: UploadCompletionState
	completionLabel: string
}

export const isUploadActive = (status: UploadStatus) =>
	status === 'pending' || status === 'uploading'

const getCompletionLabel = (
	completionState: UploadCompletionState,
	canceledFilesCount: number,
) => {
	if (completionState === 'failed') return 'No files uploaded successfully'
	if (completionState === 'warning') return 'Uploads completed with errors'
	if (canceledFilesCount > 0) return 'Uploads completed with canceled items'
	return 'All uploads completed'
}

export const getUploadProgressSummary = (
	items: UploadFileStatus[],
	discoveryState: UploadDiscoveryState,
): UploadProgressSummary => {
	const fileItems = items.filter(
		(item) => item.nodeType !== 'folder' && item.file,
	)
	const counts = fileItems.reduce<UploadStatusCounts>(
		(result, item) => {
			result[item.status] += 1
			return result
		},
		{
			pending: 0,
			uploading: 0,
			completed: 0,
			failed: 0,
			canceled: 0,
		},
	)
	const processedProgress = fileItems.reduce((total, item) => {
		const progress =
			item.status === 'completed' ||
			item.status === 'failed' ||
			item.status === 'canceled'
				? 100
				: item.progress
		return total + progress
	}, 0)
	const undiscoveredFilesCount = discoveryState.isDiscovering
		? Math.max(
				discoveryState.totalFiles - discoveryState.processedFiles,
				0,
			)
		: 0
	const totalFilesCount = fileItems.length + undiscoveredFilesCount
	const overallProgress = totalFilesCount
		? Math.round(processedProgress / totalFilesCount)
		: 0
	const hasActiveUploads = items.some((item) => isUploadActive(item.status))
	const isUploadFinished =
		!discoveryState.isDiscovering && fileItems.length > 0 && !hasActiveUploads
	const completionState = !isUploadFinished
		? null
		: counts.completed === 0 && counts.failed > 0
			? 'failed'
			: counts.failed > 0
				? 'warning'
				: 'success'

	return {
		fileItems,
		counts,
		overallProgress,
		hasActiveUploads,
		isUploadFinished,
		completionState,
		completionLabel: getCompletionLabel(completionState, counts.canceled),
	}
}

type FolderOutcomeCounts = {
	hasPending: boolean
	hasUploading: boolean
	hasCompleted: boolean
	hasFailed: boolean
	hasCanceled: boolean
}

const getFolderOutcome = (counts: FolderOutcomeCounts): FolderUploadOutcome => {
	if (counts.hasPending || counts.hasUploading) {
		return {
			status: counts.hasUploading ? 'uploading' : 'pending',
			isWarning: false,
			label: counts.hasUploading ? 'Upload in progress' : 'Waiting to upload',
		}
	}
	if (counts.hasCompleted && counts.hasFailed) {
		return {
			status: 'completed',
			isWarning: true,
			label: 'Some items uploaded successfully, some failed',
		}
	}
	if (counts.hasCompleted) {
		return {
			status: 'completed',
			isWarning: false,
			label: counts.hasCanceled
				? 'Some items uploaded successfully; remaining items were canceled'
				: 'All items uploaded successfully',
		}
	}
	if (counts.hasFailed) {
		return {
			status: 'failed',
			isWarning: false,
			label: 'No items uploaded successfully',
		}
	}
	if (counts.hasCanceled) {
		return {
			status: 'canceled',
			isWarning: false,
			label: 'All items were canceled',
		}
	}

	return {
		status: 'pending',
		isWarning: false,
		label: 'Waiting to upload',
	}
}

// Folder's builder outcomes bottom-up (parent reflects every file below it)
export const getFolderUploadOutcomes = (items: UploadFileStatus[]) => {
	const itemsById = new Map(items.map((item) => [item.id, item]))
	const outcomes = new Map<string, FolderUploadOutcome>()
	const summaries = new Map<string, FolderOutcomeCounts>()
	const folders = items
		.filter((item) => item.nodeType === 'folder' && item.id)
		.sort((first, second) => (second.depth || 0) - (first.depth || 0))

	folders.forEach((folder) => {
		const childItems = (folder.childrenIds || [])
			.map((childId) => itemsById.get(childId))
			.filter((child): child is UploadFileStatus => Boolean(child))

		const counts: FolderOutcomeCounts = {
			hasPending: false,
			hasUploading: false,
			hasCompleted: false,
			hasFailed: false,
			hasCanceled: false,
		}

		const addStatus = (status: UploadStatus) => {
			if (status === 'pending') counts.hasPending = true
			if (status === 'uploading') counts.hasUploading = true
			if (status === 'completed') counts.hasCompleted = true
			if (status === 'failed') counts.hasFailed = true
			if (status === 'canceled') counts.hasCanceled = true
		}
		const addSummary = (summary: FolderOutcomeCounts) => {
			counts.hasPending ||= summary.hasPending
			counts.hasUploading ||= summary.hasUploading
			counts.hasCompleted ||= summary.hasCompleted
			counts.hasFailed ||= summary.hasFailed
			counts.hasCanceled ||= summary.hasCanceled
		}

		if (folder.status === 'failed') counts.hasFailed = true

		if (childItems.length === 0) {
			addStatus(folder.status)
		} else {
			childItems.forEach((child) => {
				if (child.nodeType !== 'folder') {
					addStatus(child.status)
					return
				}

				const childSummary = child.id ? summaries.get(child.id) : undefined
				if (childSummary) addSummary(childSummary)
				else addStatus(child.status)
			})
		}

		summaries.set(folder.id!, counts)
		outcomes.set(folder.id!, getFolderOutcome(counts))
	})

	return outcomes
}
